import { INotificationMessage } from "@fl/shared/src/types/messages";
import {
  PaginasMenuAttributes,
  UpdatePaginasMenu,
} from "@fl/shared/src/types/models/suporte_fl/paginas_menus";
import {
  GrupoPermissaoAttributes,
  PermissaoAttributes,
  UpdateGrupoPermissao,
} from "@fl/shared/src/types/models/suporte_fl/permissoes";
import { UsuarioAttributes } from "@fl/shared/src/types/models/suporte_fl/usuarios";
import { IReturnHTTP } from "@fl/shared/src/types/returns";
import { ITableDataColumns } from "@fl/shared/src/types/tables";
import { useEffect, useState } from "react";
import { Button, Container } from "react-bootstrap";
import {
  MdDelete,
  MdDeleteOutline,
  MdModeEdit,
  MdOutlineEdit,
} from "react-icons/md";
import { RiGroupFill, RiGroupLine } from "react-icons/ri";
import { VscListTree, VscListUnordered } from "react-icons/vsc";
import ModalGroupPermissions from "../../../components/permissions/modalGroup";
import ModalPermissionsMenus from "../../../components/permissions/modalPermissions";
import ModalPermissionsUsersMenus from "../../../components/permissions/modalUsers";
import TableData from "../../../components/tables";
import useConfirm from "../../../hooks/useConfirm";
import ApiMenus from "../../../services/suporte/auth/menus";
import ApiPermissions from "../../../services/suporte/auth/permissions";
import DefaultBasePage from "../../default/base";

const ConfigPermissionsPrivatePage = () => {
  const columnsTable: ITableDataColumns[] = [
    {
      columnKey: "id_grupo_permissao",
      label: "#ID",
      pk: true,
      alignText: "text-center",
    },
    { columnKey: "nome", label: "Grupo", alignText: "text-start" },
    { columnKey: "descricao", label: "Descrição", alignText: "text-start" },
    {
      columnKey: "createdAt",
      label: "Data de Criação",
      dateColumns: true,
      alignText: "text-center",
    },
    {
      columnKey: "{actions}",
      label: "Ação",
      action: true,
      alignText: "text-center",
      actionButtons: [
        {
          label: "Editar Grupo",
          icon: <MdOutlineEdit size={15} />,
          iconHover: <MdModeEdit size={15} />,
          hover: false,
          action: (item) => onEditGroup(item),
        },
        {
          label: "Editar Permissões [Menus]",
          icon: <VscListUnordered size={15} />,
          iconHover: <VscListTree size={15} />,
          hover: false,
          action: (item) => onEdiPermissions(item),
        },
        {
          label: "Editar Usuários",
          icon: <RiGroupLine size={15} />,
          iconHover: <RiGroupFill size={15} />,
          hover: false,
          action: (item) => onEditUsers(item),
        },
        {
          label: "Excluir Grupo",
          icon: <MdDeleteOutline size={17.5} />,
          iconHover: <MdDelete size={17.5} />,
          hover: false,
          action: (item) => onDeleteGroup(item),
        },
      ],
    },
  ];

  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState<INotificationMessage[]>([]);
  const [showModalGroupPermissions, setShowModalGroupPermissions] =
    useState(false);
  const [showModalPermissionsMenus, setShowModalPermissionsMenus] =
    useState(false);
  const [showModalUsersPermissionsMenus, setShowModalUsersPermissionsMenus] =
    useState(false);
  const [groupPermissions, setGroupPermissions] = useState<
    GrupoPermissaoAttributes[]
  >([]);
  const [group, setGroup] = useState<UpdateGrupoPermissao>();
  const [selectedGroup, setSelectedGroup] =
    useState<GrupoPermissaoAttributes | null>(null);
  const [baseMenus, setBaseMenus] = useState<UpdatePaginasMenu[]>([]);

  useEffect(() => {
    if (groupPermissions.length === 0) loadGroupPermissions();
  }, []);

  useEffect(() => {
    const loadMenus = async () => {
      const result = await new ApiMenus().list();
      if (result.success) {
        const _menus = result.data.map((item: PaginasMenuAttributes) => {
          return {
            ...item,
            checked: false,
            collapsed: false,
            new: false,
            edited: false,
          };
        });
        setBaseMenus(_menus);
      }
    };

    loadMenus();
  }, []);

  const loadGroupPermissions = async () => {
    try {
      setLoading(true);
      const result = await new ApiPermissions().listGroup();
      if (result.success) setGroupPermissions(result.data);
    } finally {
      setLoading(false);
    }
  };

  const onEditGroup = (item: GrupoPermissaoAttributes) => {
    setGroup({ ...item, new: false });
    setShowModalGroupPermissions(true);
  };

  const onEdiPermissions = (item: GrupoPermissaoAttributes) => {
    setSelectedGroup({ ...item });
    setShowModalPermissionsMenus(true);
  };

  const onEditUsers = (item: GrupoPermissaoAttributes) => {
    setSelectedGroup({ ...item });
    setShowModalUsersPermissionsMenus(true);
  };

  const onDeleteGroup = (item: GrupoPermissaoAttributes) => {
    handleShowGroup(
      "Confirmar exclusão de grupo?",
      `Tem certeza que deseja excluir o grupo de permissões "<b>${item.nome}</b>"?`,
      item
    );
  };

  const onUpdateGroup = async (item: UpdateGrupoPermissao) => {
    try {
      setLoading(true);
      var result: IReturnHTTP | null = null;

      if (item.new) {
        result = await new ApiPermissions({ ...item }).createGroup();
      } else {
        result = await new ApiPermissions({ ...item }).updateGroup();
      }

      if (result.success) {
        const groups = [...groupPermissions];
        if (item.new) {
          setGroupPermissions([...groups, { ...result.data }]);
        } else {
          const index = groups.findIndex(
            (i) => item.id_grupo_permissao === i.id_grupo_permissao
          );
          groups[index] = { ...result.data };
          setGroupPermissions([...groups]);
        }
      } else {
        if (result.message && result.message.length > 0)
          setMessages(result.message);
      }
    } finally {
      setGroup(undefined);
      setLoading(false);
      setShowModalGroupPermissions(false);
    }
  };

  const onUpdatePermissions = async (
    id_grupo_permissao: number,
    updated_permissions: PermissaoAttributes[]
  ) => {
    try {
      setLoading(true);
      const result = await new ApiPermissions({
        id_grupo_permissao,
        updated_permissions,
      }).updatePermissions();
      if (!result.success) {
        if (result.message && result.message.length > 0)
          setMessages(result.message);
      }
    } finally {
      setLoading(false);
      setShowModalPermissionsMenus(false);
    }
  };

  const onUpdateUsersPermissions = async (
    updated_users: UsuarioAttributes[]
  ) => {
    try {
      setLoading(true);
      const result = await new ApiPermissions({
        updated_users,
      }).updateUsers();
      if (!result.success) {
        if (result.message && result.message.length > 0)
          setMessages(result.message);
      }
    } finally {
      setLoading(false);
      setShowModalUsersPermissionsMenus(false);
    }
  };

  const submitDeleteGroup = async (item: GrupoPermissaoAttributes) => {
    try {
      setLoading(true);
      const result = await new ApiPermissions({
        id_grupo_permissao: item.id_grupo_permissao,
      }).deleteGroup();

      if (result.success) {
        const updatedGroups = groupPermissions.filter(
          (group) => group.id_grupo_permissao !== item.id_grupo_permissao
        );
        setGroupPermissions([...updatedGroups]);
      } else {
        if (result.message && result.message.length > 0)
          setMessages(result.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const { handleShow: handleShowGroup, ConfirmModalComponent: ConfirmModal } =
    useConfirm({
      onConfirm: submitDeleteGroup,
    });

  return (
    <DefaultBasePage
      title="Grupos e Permissões"
      loading={loading}
      message={messages}
    >
      <Container className="d-flex vertical-center my-3">
        <Button
          variant="primary"
          onClick={() => {
            setGroup({ new: true, nome: "", descricao: "" });
            setShowModalGroupPermissions(true);
          }}
        >
          Adicionar Grupo de Permissões
        </Button>
      </Container>

      <TableData columns={columnsTable} data={groupPermissions} />

      <ModalGroupPermissions
        showModal={showModalGroupPermissions}
        setShowModal={setShowModalGroupPermissions}
        data={group}
        setData={setGroup}
        onUpdate={onUpdateGroup}
      />

      <ModalPermissionsMenus
        showModal={showModalPermissionsMenus}
        setShowModal={setShowModalPermissionsMenus}
        selectedGroup={selectedGroup}
        baseMenus={baseMenus}
        onUpdatePermissions={onUpdatePermissions}
      />

      <ModalPermissionsUsersMenus
        showModal={showModalUsersPermissionsMenus}
        setShowModal={setShowModalUsersPermissionsMenus}
        selectedGroup={selectedGroup}
        onUpdateUsersPermissions={onUpdateUsersPermissions}
      />

      {ConfirmModal}
    </DefaultBasePage>
  );
};

export default ConfigPermissionsPrivatePage;
